import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';

import { TableHeaderColumn, TableProps } from '@kanda-libs/ks-component-ts';
import { StringIndexedObject } from '@kanda-libs/ks-design-library';

import { URLS } from 'components/shared/App/constants';
import usePagination from 'hooks/usePagination';
import useSearchQuery from 'hooks/useSearchQuery';
import { selectors } from 'store';
import partnerSelectors from 'store/selectors/partner';

import { SEARCH_OPTIONS, TABLE_COLUMNS } from './constants';
import usePartnerTableSorting from './usePartnerTableSorting';

export type PartnerTablePropsHook = TableProps;

export default function usePartnerTableProps(): PartnerTablePropsHook {
  const { push } = useHistory();

  const { onAction } = usePartnerTableSorting();

  const partners = useSelector(partnerSelectors.getFilteredPartners);

  const { hits, query } = useSearchQuery(partners, 'partners', SEARCH_OPTIONS);
  const isLoading = useSelector(selectors.getIsLoading);

  const { pageIndex, perPage, totalPages, setPageIndex } = usePagination(
    hits.length,
  );

  // Data slicing is used in place of API pagination - in real use, use API
  // pagination
  const paginatedData = useMemo(() => {
    return (
      hits?.slice(pageIndex * perPage, pageIndex * perPage + perPage) || []
    ).map(({ item }) => item);
  }, [hits, pageIndex, perPage]);

  const setPage = useCallback(
    (index: number) => setPageIndex(index),
    [setPageIndex],
  );

  const queryRef = useRef('');

  // Effect resets page index to 0 when query changes - when user searches
  useEffect(() => {
    if (queryRef.current && queryRef.current === query) return;
    setPageIndex(0);
  }, [query, setPageIndex]);

  const onRowClicked = useCallback(
    (row?: StringIndexedObject) => {
      if (row) {
        push(URLS.partner.replace(':id', row.original.id));
      }
    },
    [push],
  );

  return {
    data: paginatedData,
    onRowClicked,
    pageIndex,
    totalPages,
    setPage,
    columns: TABLE_COLUMNS as unknown as TableHeaderColumn[],
    isLoading,
    onAction,
  };
}
